import FindInPageIcon from "@mui/icons-material/FindInPage";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PrintIcon from "@mui/icons-material/Print";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDownloadEmailTemplate } from "../../api/useDownloadEmailTemplate";
import { useDownloadDocument, usePrintDoc } from "../../api/usePrintDoc";
import { useModal } from "../../components/Modal/useModal";
import { ProductPrintConfiguration } from "../../data/global/useProductConfiguration";
import { HCPSpecificMessage, Message, PrintDoc } from "../../generate";
import EmailSellSheetModal from "./EmailSellSheetModal";

interface Props {
  client: string;
  hcp: HCPSpecificMessage;
  sellSheet: Message;
  printConfig?: ProductPrintConfiguration;
}

const EmailSellSheetRow: React.FC<Props> = ({ client, hcp, sellSheet, printConfig }) => {
  const [t] = useTranslation();
  const { setModal } = useModal();

  const printDoc: PrintDoc = {
    client,
    baseDocName: sellSheet.messageName,
    prescID: hcp.prescID,
    includePI: sellSheet.enablePI !== "N",
    isResource: false,
  };

  const showPrintButton = printConfig?.showPrintIconButton === true;
  const showEmailButton = printConfig?.showEmailIconButton === true;

  const { isLoading: isDownloading, mutateAsync: downloadEmailTemplate } = useDownloadEmailTemplate(printDoc, hcp);

  const printDocForPdf: PrintDoc = {
    ...printDoc,
    isPrint: false,
  };

  const document = usePrintDoc({ printDoc: printDocForPdf });

  const { isLoading: isDownloadingPdf, mutateAsync: downloadDocument } = useDownloadDocument();

  const handlePrintClick = async () => {
    const url = document.data && document.data.downloadURL ? document.data.downloadURL : "";
    if (url) {
      await downloadDocument({ printDoc: printDocForPdf, hcp, url });
    }
  };

  const buttonClasses = classNames(
    "flex items-center justify-center h-10 w-10 text-primary rounded-full focus:outline-none focus:ring hover:bg-light-gray",
  );
  return (
    <div className="flex  flex-col-reverse sm:flex-row sm:items-center pt-1 hover:bg-light-blue -ml-3">
      <div className="pl-3 sm:pl-0 sm:w-56 flex space-x-1 border-b border-rules sm:border-none">
        <button
          className={buttonClasses}
          aria-label={t("specificMessages.downloadPrintPdf")}
          onClick={() => setModal(<EmailSellSheetModal client={client} hcp={hcp} sellSheet={sellSheet} />)}
        >
          <FindInPageIcon />
        </button>
        {showEmailButton && (
          <button disabled={isDownloading} className={buttonClasses} onClick={() => downloadEmailTemplate()}>
            <MailOutlineIcon />
          </button>
        )}
        {showPrintButton && (
          <button
            disabled={document.isLoading || isDownloadingPdf}
            className={buttonClasses}
            aria-label={t("specificMessages.downloadPrintPdf")}
            onClick={handlePrintClick}
          >
            <PrintIcon />
          </button>
        )}
      </div>
      <div className="pl-6 sm:pl-3 py-2">
        <div className="text-xs sm:text-sm">{sellSheet.messageName}</div>
      </div>
    </div>
  );
};

export default EmailSellSheetRow;
